import React, { useState, useEffect } from 'react';

// Import components
import Header from './Header';
import ProjectStatusCard from './ProjectStatusCard';
import DepartmentGrid from './DepartmentGrid';
import MobileSummary from './MobileSummary';
import ProductionTimeline from './ProductionTimeline';
import TaskFormWrapper from './TaskFormWrapper';
import LoadingSpinner from '../LoadingSpinner';

// Import services and utilities
import api from '../../services/api';
import { 
  mapAirtableProject, mapAirtableDepartment, mapAirtableTask, prepareTaskForAirtable 
} from '../../utils/dataMappers';
import { getIconComponent, getProjectStatusInfo } from '../../utils/icons';

// Sample fallback data
import { sampleProjects, sampleDepartments } from '../../data/sampleData';

const AnimationProductionTracker = () => {
  // State variables
  const [expandedDepartment, setExpandedDepartment] = useState('Animation');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [viewMode, setViewMode] = useState('desktop');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  
  // API data state
  const [apiProjects, setApiProjects] = useState([]);
  const [apiDepartments, setApiDepartments] = useState([]);
  const [apiTasks, setApiTasks] = useState([]);
  const [apiTeam, setApiTeam] = useState([]);
  const [apiMilestones, setApiMilestones] = useState([]);
  
  // Detect device/screen size
  useEffect(() => {
    setIsLoaded(true);
    
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('mobile');
      } else if (window.innerWidth > 1920) {
        setViewMode('tv');
      } else {
        setViewMode('desktop');
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load data from API
  useEffect(() => {
    fetchData();
  }, []);

  // Set initial selected project once data is loaded
  useEffect(() => {
    if (apiProjects.length > 0 && !selectedProject) {
      const projects = apiProjects.map(mapAirtableProject).filter(Boolean);
      if (projects.length > 0) {
        setSelectedProject(projects[0].name);
      }
    }
  }, [apiProjects, selectedProject]);
  
  // Fetch data function
  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load all data in parallel
      const [projectsData, departmentsData, tasksData, teamData, milestonesData] = await Promise.all([
        api.getProjects(),
        api.getDepartments(),
        api.getTasks(),
        api.getTeam(),
        api.getMilestones()
      ]);
      
      setApiProjects(projectsData);
      setApiDepartments(departmentsData);
      setApiTasks(tasksData);
      setApiTeam(teamData);
      setApiMilestones(milestonesData);
      
      // If we got any empty data from the API, show an error
      if (projectsData.length === 0 || departmentsData.length === 0) {
        setError('Unable to load data. Please check your connection.');
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create task
  const createTask = async (taskData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Prepare Airtable data
      const airtableData = prepareTaskForAirtable(taskData);
      
      // Create task
      await api.createTask(airtableData);
      
      // Refresh data
      await fetchData();
      
      // Hide form
      setShowTaskForm(false);
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Failed to create task. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update task status
  // Update task status
const updateTaskStatus = async (taskId, newStatus) => {
  setIsLoading(true);
  setError(null);
  
  try {
    // Map status to exactly match Airtable's options
    let statusForAirtable;
    let progress = 0;
    
    if (newStatus === 'completed' || newStatus === 'complete') {
      statusForAirtable = 'Completed'; // CHANGED FROM 'Complete' to 'Completed'
      progress = 100;
    } else if (newStatus === 'in-progress') {
      statusForAirtable = 'In Progress';
      progress = 50;
    } else {
      statusForAirtable = 'Not Started';
    }
    
    console.log(`Sending status update: ${newStatus} → ${statusForAirtable}`);
    
    // Prepare Airtable data
    const airtableData = {
      Status: statusForAirtable,
      Progress: progress
    };
    
    // Update task
    await api.updateTask(taskId, airtableData);
    
    // Refresh data
    await fetchData();
  } catch (error) {
    console.error('Error updating task:', error);
    setError('Failed to update task status. Please try again.');
  } finally {
    setIsLoading(false);
  }
};
  
  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }).format(date);
  };
  
  // Toggle department expansion
  const toggleDepartment = (deptName) => {
    if (expandedDepartment === deptName) {
      setExpandedDepartment(null);
    } else {
      setExpandedDepartment(deptName);
    }
  };
  
  // Map Airtable data to app format
  // 1. First define projectsToUse
    const projectsToUse = apiProjects.length > 0 
      ? apiProjects.map(mapAirtableProject).filter(Boolean)
      : sampleProjects;

    const currentProject = projectsToUse.find(p => p.name === selectedProject) || projectsToUse[0];

    
  // Get departments with tasks
const getDepartmentsWithTasks = () => {
  if (apiDepartments.length > 0 && apiTasks.length > 0) {
    // Map departments
    const mappedDepts = apiDepartments.map(mapAirtableDepartment).filter(Boolean);
    
    // For each department, find its tasks
    return mappedDepts.map(dept => {
      // Map tasks and filter for this department AND current project
      const deptTasks = apiTasks
        .filter(task => {
          // Debug the filtering to see what's happening
          console.log('Filtering task:', task.fields.Name, 
                     'Department:', task.fields.Department, 
                     'Project:', task.fields.Project,
                     'Looking for dept:', dept.id,
                     'Looking for project:', currentProject?.id);
          
          // Make sure we're checking both conditions correctly
          return task.fields.Department && 
                 (Array.isArray(task.fields.Department) ? 
                   task.fields.Department.includes(dept.id) : 
                   task.fields.Department === dept.id) && 
                 task.fields.Project && 
                 (Array.isArray(task.fields.Project) ? 
                   task.fields.Project.includes(currentProject?.id) : 
                   task.fields.Project === currentProject?.id);
        })
        .map(task => mapAirtableTask(task, apiDepartments, apiTeam))
        .filter(Boolean);
          
      // Add tasks to department
      return {
        ...dept,
        icon: getIconComponent(dept.icon),
        tasks: deptTasks
      };
    });
  }
  
  // Fallback to sample data
  return sampleDepartments;
};

const departmentsToUse = getDepartmentsWithTasks(currentProject);


  
  // Get upcoming milestones
  const getUpcomingMilestones = () => {
    if (apiMilestones.length > 0) {
      // Implementation would filter and map real milestone data
      return apiMilestones
        .filter(m => new Date(m.fields.Date) > new Date())
        .map(m => ({
          name: m.fields.Name,
          date: m.fields.Date,
          days: Math.ceil((new Date(m.fields.Date) - new Date()) / (1000 * 60 * 60 * 24))
        }))
        .sort((a, b) => a.days - b.days)
        .slice(0, 3);
    }
    
    
    if (currentProject) {
      return [
        { name: 'Animation Review', date: '2025-05-25', days: 9 },
        { name: 'Lighting Setup', date: '2025-06-10', days: 25 }
      ];
    }
    
    return [];
  };
  
  // Calculate overall project completion
  const calculateOverallProgress = () => {
    let totalTasks = 0;
    let completedTasksValue = 0;
    
    departmentsToUse.forEach(dept => {
      dept.tasks.forEach(task => {
        totalTasks++;
        completedTasksValue += task.progress || 0;
      });
    });
    
    return totalTasks > 0 ? Math.round(completedTasksValue / totalTasks) : 0;
  };
  
  // Get department progress
  const getDepartmentProgress = (deptTasks) => {
    if (!deptTasks || deptTasks.length === 0) return 0;
    
    const totalProgress = deptTasks.reduce((sum, task) => sum + (task.progress || 0), 0);
    return Math.round(totalProgress / deptTasks.length);
  };
  
  // Get derived data using the functions above
  const projectStatus = getProjectStatusInfo(currentProject?.status || 'on-track');
  const upcomingMilestones = getUpcomingMilestones();
  const overallProgress = calculateOverallProgress();

  return (
    <div className="flex flex-col h-full bg-gray-50 text-gray-800 transition-all duration-300">
      <Header 
        viewMode={viewMode}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        projectsToUse={projectsToUse}
        fetchData={fetchData}
        isLoading={isLoading}
        overallProgress={overallProgress}
      />
      
      <ProjectStatusCard 
        currentProject={currentProject}
        projectStatus={projectStatus}
        formatDate={formatDate}
        upcomingMilestones={upcomingMilestones}
        viewMode={viewMode}
      />
      
      <TaskFormWrapper
        showTaskForm={showTaskForm}
        setShowTaskForm={setShowTaskForm}
        apiDepartments={apiDepartments}
        apiTeam={apiTeam}
        selectedProject={selectedProject}
        apiProjects={apiProjects}
        createTask={createTask}
      />
      
      {/* Error Message */}
      {error && (
        <div className="mx-auto my-4 max-w-7xl px-4 md:px-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
            <div className="flex">
              <div className="py-1"><svg className="fill-current h-6 w-6 text-red-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/></svg></div>
              <div>
                <p className="font-medium">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 pb-8 pt-4">
  <DepartmentGrid 
    departmentsToUse={departmentsToUse}
    getDepartmentProgress={getDepartmentProgress}
    expandedDepartment={expandedDepartment}
    toggleDepartment={toggleDepartment}
    formatDate={formatDate}
    updateTaskStatus={updateTaskStatus}
    viewMode={viewMode}
  />
  
  {viewMode === 'mobile' && (
    <MobileSummary 
      departmentsToUse={departmentsToUse}
      getDepartmentProgress={getDepartmentProgress}
    />
  )}
  
  {/* Update this line to pass all required props */}
  <ProductionTimeline 
    viewMode={viewMode}
    departmentsToUse={departmentsToUse} 
    upcomingMilestones={upcomingMilestones}
    currentProject={currentProject}
  />
</div>
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default AnimationProductionTracker;
